import { format } from "date-fns";
import { useState } from "react";
import { useParams } from "react-router";
import MainLayout from "@/components/layouts";
import { useSubsDetail } from "./subs-detail.hooks";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { getInitials } from "@/lib/utils";
import { Building2, Mail, Phone, MapPin, Users, Settings, UserPlus, Edit, File, FileDown, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactFormModal } from "./components/contact-form-modal";
import DocumentUploadModal from "@/components/shared/upload-document/document-upload-modal";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import type { DocumentRecord } from "@shyne/shared/api";
import { DocumentOpenLink } from "@/components/shared/documents/document-open-link";
import { DocumentDeleteButton } from "@/components/shared/documents/document-delete-button";
import { shownDay } from "@/lib/documents";
import { DailyWorkHourlyRateCard } from "@/components/daily-work/DailyWorkHourlyRateCard";

export default function SubsDetailPage() {
  const { subsId } = useParams<{ subsId: string }>();
  const {
    subsDetail,
    loading,
    updateSubsServices,
    availableServices,
    servicesLoading,
    selectedServices,
    hasChanges,
    handleToggleService,
    handleSaveServices,
    primaryContact,
    contactModalOpen,
    contactSaving,
    documents,
    documentsLoading,
    documentsTotal,
    documentsPage,
    documentsPageSize,
    uploadModalOpen,
    uploadingDocument,
    deletingDocumentId,
    updatingDocumentId,
    handleOpenContactModal,
    handleCloseContactModal,
    handleSaveContact,
    setUploadDocumentModalOpen,
    setDocumentsPage,
    setDocumentsPageSize,
    uploadDocument,
    deleteDocument,
    updateDocumentExpiresAt,
  } = useSubsDetail(subsId!);
  const [editingDocument, setEditingDocument] = useState<DocumentRecord | null>(null);
  const [expiresAtValue, setExpiresAtValue] = useState("");

  const openExpiresAtEditor = (document: DocumentRecord) => {
    setEditingDocument(document);
    setExpiresAtValue(document.expiresOn ?? "");
  };

  const closeExpiresAtEditor = () => {
    setEditingDocument(null);
    setExpiresAtValue("");
  };

  const handleSaveExpiresAt = async () => {
    if (!editingDocument) return;

    const saved = await updateDocumentExpiresAt(editingDocument.id, expiresAtValue || null).then(
      () => true,
      () => false
    );
    if (saved) closeExpiresAtEditor();
  };

  if (loading) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Subs", href: "/subs" },
          { name: "Loading...", href: "#" },
        ]}
      >
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-[200px]" />
              <Skeleton className="h-4 w-[150px]" />
            </div>
          </div>
          <Skeleton className="h-[200px] w-full" />
        </div>
      </MainLayout>
    );
  }

  if (!subsDetail) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Subs", href: "/subs" },
          { name: "Not Found", href: "#" },
        ]}
      >
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold">Subs Organization Not Found</h2>
          <p className="text-muted-foreground mt-2">
            The subs organization you're looking for doesn't exist or you don't have access to it.
          </p>
        </div>
      </MainLayout>
    );
  }

  const { subcontractor: subs } = subsDetail;
  const ended = subsDetail.endedAt !== null;

  const documentReadColumns: ColumnDef<DocumentRecord>[] = [
    {
      header: "Document",
      accessor: (document) => <DocumentOpenLink document={document} />,
    },
    {
      header: "Uploaded",
      accessor: (document) => format(new Date(document.uploadedAt), "MM/dd/yyyy"),
    },
    {
      header: "Expires",
      accessor: (document) => shownDay(document.expiresOn),
    },
  ];

  const documentColumns: ColumnDef<DocumentRecord>[] = ended ? documentReadColumns : [
    ...documentReadColumns,
    {
      header: "Actions",
      accessor: (document) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Edit expiration of ${document.filename}`}
            onClick={() => openExpiresAtEditor(document)}
            disabled={updatingDocumentId === document.id}
          >
            {updatingDocumentId === document.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Edit className="h-4 w-4" />
            )}
          </Button>
          <DocumentDeleteButton document={document} deleting={deletingDocumentId === document.id} onDelete={deleteDocument} />
        </div>
      ),
      className: "w-28 justify-end",
    },
  ];

  return (
    <MainLayout
      breadcrumb={[
        { name: "Subs", href: "/subs" },
        { name: subs.name, href: "#" },
      ]}
      pageHeader={{
        title: subs.name,
        subtitle: subs.description || "Subs organization",
        buttonText: hasChanges && !ended ? "Save" : undefined,
        onButtonClick: hasChanges && !ended ? handleSaveServices : undefined,
      }}
    >
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Organization Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Organization Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={subs.logo ?? undefined} />
                    <AvatarFallback className="text-lg">{getInitials(subs.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-xl font-semibold">{subs.name}</h3>
                    <p className="text-muted-foreground">{subs.email}</p>
                    <p className="text-sm text-muted-foreground" data-testid="subcontract-payment-terms">
                      Payment terms: Net {subsDetail.paymentTermsDays}
                    </p>
                    {subsDetail.endedAt && (
                      <p className="text-sm font-medium text-destructive" data-testid="subcontract-ended">
                        Removed on {format(new Date(subsDetail.endedAt), "MMM d, yyyy")}
                      </p>
                    )}
                    {subs.location && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {subs.location}
                      </p>
                    )}
                  </div>
                </div>

                {subs.description && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2">Description</h4>
                      <p className="text-muted-foreground">{subs.description}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {!ended && (
              <DailyWorkHourlyRateCard
                payer={subsDetail.vendor.id}
                payee={{ subcontractor: subs.id }}
                services={availableServices.map((service) => ({ id: service.id, name: service.name }))}
                testIdPrefix={`daily-work-rate-subs-${subsDetail.id}`}
              />
            )}

            {/* Services */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Services
                </CardTitle>
                <CardDescription>Select services this subs organization can provide</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {servicesLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="flex items-center space-x-2 p-4 border rounded-lg">
                          <Skeleton className="h-4 w-4" />
                          <Skeleton className="h-4 w-24" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {availableServices.map((service) => {
                        const isSelected = selectedServices.includes(service.id);

                        return (
                          <label
                            key={service.id}
                            htmlFor={service.id}
                            className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          >
                            <Checkbox
                              id={service.id}
                              checked={isSelected}
                              disabled={ended}
                              onCheckedChange={() => handleToggleService(service.id)}
                            />
                            <span className="text-sm font-medium leading-none flex-1 flex items-center gap-2">
                              <File className="h-6 w-6" strokeWidth={1} />
                              {service.name}
                            </span>
                          </label>
                        );
                      })}
                      {availableServices.length === 0 && (
                        <p className="text-muted-foreground col-span-2">No services available</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            {/* Primary Contact */}
            {primaryContact ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Primary Contact
                    </div>
                    {!ended && (
                      <Button variant="ghost" size="sm" onClick={handleOpenContactModal} className="h-8 w-8 p-0" aria-label="Edit primary contact">
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h4 className="font-medium">{primaryContact.name}</h4>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {primaryContact.email}
                    </p>
                    {primaryContact.phone && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {primaryContact.phone}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Primary Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <UserPlus className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-4">No primary contact added yet</p>
                    {!ended && (
                      <Button onClick={handleOpenContactModal} size="sm">
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Contact
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileDown className="h-5 w-5" />
                  Documents
                </div>
                {!ended && (
                  <Button variant="outline" size="sm" onClick={() => setUploadDocumentModalOpen(true)}>
                    Upload Document
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documentsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : documents.length > 0 ? (
                <SimpleTable<DocumentRecord>
                  columns={documentColumns}
                  data={documents}
                  totalCount={documentsTotal}
                  page={documentsPage}
                  pageSize={documentsPageSize}
                  setPage={setDocumentsPage}
                  setPageSize={setDocumentsPageSize}
                  filters={[]}
                  onReset={() => {}}
                />
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileDown className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">No Documents</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    No documents have been uploaded for this company yet.
                  </p>
                  {!ended && <Button onClick={() => setUploadDocumentModalOpen(true)}>Upload First Document</Button>}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ContactFormModal
        open={contactModalOpen}
        onOpenChange={handleCloseContactModal}
        onSubmit={handleSaveContact}
        initialContact={primaryContact}
        saving={contactSaving}
      />
      <DocumentUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadDocumentModalOpen}
        onUpload={uploadDocument}
        uploading={uploadingDocument}
      />
      <Dialog open={!!editingDocument} onOpenChange={(open) => (!open ? closeExpiresAtEditor() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Expiration Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="expires-at">Expires At</Label>
            <Input
              id="expires-at"
              type="date"
              value={expiresAtValue}
              onChange={(event) => setExpiresAtValue(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeExpiresAtEditor}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => setExpiresAtValue("")}>
              Clear Date
            </Button>
            <Button onClick={handleSaveExpiresAt} disabled={updatingDocumentId === editingDocument?.id}>
              {updatingDocumentId === editingDocument?.id && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
